<script>
  import { onMount } from 'svelte';
  import { writable } from 'svelte/store';
  import { API_BASE_URL } from '../config.js';

  let userId = writable(''); // Store the Telegram user ID
  let name = writable('');
  let selectedCategories = writable([]);
  let categories = writable([]);
  let link = writable('');
  let thumbnail = writable('');
  let comments = writable('');
  let errorMessage = writable('');
  let showModal = writable(true); // For the recommendations popup

  // Initialize Telegram WebApp
  onMount(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      // Extract user info from Telegram WebApp
      const tgUser = window.Telegram.WebApp.initDataUnsafe?.user;
      if (tgUser) {
        userId.set(tgUser.id); // Set user ID
      }
    }
    loadCategories();
  });

  // Fetch categories from the backend
  async function fetchCategories(parentID = 0) {
    const response = await fetch(`${API_BASE_URL}/categories?parent_id=${parentID}`);
    if (!response.ok) {
      errorMessage.set('Failed to load categories.');
      return [];
    }
    return await response.json();
  }

  async function loadCategories(parentID = 0, level = 0) {
    const fetchedCategories = await fetchCategories(parentID);
    if (fetchedCategories.length > 0) {
      categories.update(current => {
        current[level] = fetchedCategories;
        return current;
      });
    }
  }

  function handleCategoryChange(event, level) {
    const selectedCategoryID = parseInt(event.target.value);

    selectedCategories.update(current => {
      current[level] = selectedCategoryID;
      return current.slice(0, level + 1);
    });

    loadCategories(selectedCategoryID, level + 1);
  }

  // Validate and submit the form
  function validateForm() {
    let description = $selectedCategories.map((categoryID, index) => {
      const category = $categories[index]?.find(cat => cat.id === categoryID);
      return category ? category.name : '';
    }).filter(Boolean).join(" "); // Combine all selected category names into a description

    if (!String($name).trim() || !description || !String($link).trim()) {
      errorMessage.set('All fields are required.');
      return false;
    }

    const urlPattern = /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm;
    const forbiddenPattern = /(whatsapp\.com|wa\.me)/i;

    if (!urlPattern.test($link)) {
      errorMessage.set('Invalid URL format.');
      return false;
    }

    if (forbiddenPattern.test($link)) {
      errorMessage.set('WhatsApp links are not allowed.');
      return false;
    }

    errorMessage.set('');
    return true;
  }

  async function submitSuggestion() {
    if (!validateForm()) {
      return;
    }

    const suggestion = {
      user_id: $userId, // Include Telegram user ID in the suggestion
      name: $name,
      description: $selectedCategories.length > 0 ? $categories[$categories.length - 1].find(cat => cat.id === $selectedCategories[$selectedCategories.length - 1]).name : '', // Only the final category name
      link: $link,
      category: parseInt($selectedCategories[$selectedCategories.length - 1]),
      thumbnail: $thumbnail,
      comments: $comments,
    };


    const response = await fetch(`${API_BASE_URL}/suggest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(suggestion),
    });

    if (response.ok) {
      alert('Suggestion submitted successfully!');
      name.set('');
      selectedCategories.set([]);
      link.set('');
      thumbnail.set('');
      comments.set('');
    } else {
      const errorData = await response.json();
      alert(`Failed to submit suggestion: ${errorData.message}`);
    }
  }

  // Close modal to allow form filling
  function closeModal() {
    showModal.set(false);
  }
</script>

<!-- Modal for Recommendations -->
{#if $showModal}
  <div class="modal is-active">
    <div class="modal-background"></div>
    <div class="modal-content">
      <div class="box">
        <h2 class="title">Recommendations</h2>
        <ul>
          <li>1) Make sure to set group chat history to "visible"</li>
          <li>2) Disable permission to change group info for everyone</li>
          <li>3) Create a basic set of rules for moderation</li>
          <li>4) Use a moderation helper bot</li>
          <li>5) If you are the owner of the group, make sure to let someone else have the ownership before leaving. After you leave, group link gets invalidated so make sure to resend us a new one.</li>
          <li>6) Don't let WhatsApp links circulate inside of your group.</li>
        </ul><br><br>
        Failing to comply with these recommendations won't automatically prevent your group from being added to our directory, but it will worsen your community's experience. We retain the right to reject your request and/or remove your group from the directory.
        <br><br><br>
        <h2 class="title">Mandatory requirements</h2>
        <ul>
          <li>1) Don't spam or let other people spam inside your group</li>
          <li>2) Don't publish or let other people publish unlawful content</li>
          <li>3) To use this service, you must not be banned from our Telegram network</li>
          <li>4) We may add anonymous users for spam monitoring purposes; if you ban them, we may ask you to unban them</li>
          <li>5) Your group must not be affiliated with political, religious, or unlawful organizations, nor be used to promote self-interests (e.g. political campaigns).</li>
          <li>6) Do not complain about our policies; they are enforced to maintain service quality.</li>
        </ul><br><br>
        Failing to comply with these requirements will result in rejection or removal from our list with no appeal. Unreasonable complaints will lead to a permanent ban and reporting to Telegram for further investigation.
        <br><br><br>
        <button class="button is-primary" on:click={closeModal}>I understand</button>
      </div>
    </div>
  </div>
{/if}

<!-- Form Content -->
<div class="container" class:disabled={$showModal}>
  {#if $errorMessage}
    <p class="has-text-danger">{$errorMessage}</p>
  {/if}

  <div class="field">
    <label class="label">Group Name</label>
    <div class="control">
      <input class="input" type="text" bind:value={$name} placeholder="Group Name" disabled={$showModal}>
    </div>
  </div>

  {#each $categories as levelCategories, level}
    <div class="field">
      <label class="label">Select Category Level {level + 1}</label>
      <div class="control">
        <div class="select">
          <select on:change={(e) => handleCategoryChange(e, level)} bind:value={$selectedCategories[level]} disabled={$showModal}>
            <option value="" disabled selected>Select a category</option>
            {#each levelCategories as category}
              <option value={category.id}>{category.name}</option>
            {/each}
          </select>
        </div>
      </div>
    </div>
  {/each}

  <div class="field">
    <label class="label">Group Link</label>
    <div class="control">
      <input class="input" type="text" bind:value={$link} placeholder="Group Link" disabled={$showModal}>
    </div>
  </div>

  <div class="field">
    <label class="label">Group Thumbnail (optional)</label>
    <div class="control">
      <input class="input" type="text" bind:value={$thumbnail} placeholder="Group Thumbnail" disabled={$showModal}>
    </div>
  </div>

  <div class="field">
    <label class="label">Comments (optional)</label>
    <div class="control">
      <textarea class="textarea" bind:value={$comments} placeholder="Additional comments about this group..." disabled={$showModal}></textarea>
    </div>
  </div>

  <div class="control">
    <button class="button is-primary" on:click={submitSuggestion} disabled={$showModal}>Submit</button>
  </div>
</div>
